import React, { useCallback, useEffect, useId, useMemo, useState } from "react";
import {
  getChampionReport,
  ChampionReport,
  ChampionReportItem,
  Role,
  ROLES,
} from "./proApi";
import { splashUrl, splashFallbackUrl } from "./championAssets";
import {
  Champ,
  ChampionDatalist,
  PatchSelect,
  Pct,
  pctColor,
  useProIndex,
} from "./shared";

const TOOLTIPS = {
  withAgainst: "The other champion involved in the interaction, with its role.",
  pct:
    "Percentile (0-100) of this interaction's strength within its own type — " +
    "synergies rank against synergies, matchups against matchups. " +
    "Click to flip the order.",
  n:
    "Number of competitive games (tiers 1-3) featuring this interaction " +
    "over the selected window.",
  nPatch:
    "Number of competitive games (tiers 1-3) featuring this interaction " +
    "during the selected patch.",
  recency:
    "Recency score 0-100, from the number of games featuring this " +
    "interaction over the LAST 2 WEEKS (fixed window, independent of the N " +
    "column). Scaled per interaction type so sparse types (matchups) and " +
    "dense ones stay comparable: one of the most-played interactions of its " +
    "type scores ~95, an unplayed one scores 0.",
};

function InteractionTable({
  items,
  nLabel,
  nTooltip,
  nOf,
  minRecent,
}: {
  items: ChampionReportItem[];
  nLabel: string;
  nTooltip: string;
  nOf: (it: ChampionReportItem) => number;
  minRecent: number;
}) {
  const [asc, setAsc] = useState(false);
  const filtered = useMemo(
    () => items.filter((it) => nOf(it) >= minRecent),
    [items, nOf, minRecent],
  );
  const view = asc ? [...filtered].reverse() : filtered;
  if (!filtered.length) {
    return (
      <div className="pro__empty">
        Nothing above the minimum recent games — lower the filter.
      </div>
    );
  }
  return (
    <table className="pro__table">
      <thead>
        <tr>
          <th data-tip={TOOLTIPS.withAgainst}>With / against</th>
          <th
            className="is-sortable"
            onClick={() => setAsc((v) => !v)}
            data-tip={TOOLTIPS.pct}
          >
            Percentile {asc ? "▲" : "▼"}
          </th>
          <th data-tip={nTooltip}>{nLabel}</th>
          <th data-tip={TOOLTIPS.recency}>Recency</th>
        </tr>
      </thead>
      <tbody>
        {view.map((it) => (
          <tr
            key={`${it.role}|${it.partner ?? "self"}|${it.partner_role ?? ""}`}
          >
            <td>
              <Champ name={it.partner || ""} role={it.partner_role} />
            </td>
            <td>
              <Pct value={it.pct} />
            </td>
            <td className="pro__muted">{nOf(it)}</td>
            <td>
              <Pct value={it.note} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RoleSplit({
  total,
  roles,
}: {
  total: number;
  roles: Partial<Record<Role, number>>;
}) {
  const entries = Object.entries(roles).sort((a, b) => b[1] - a[1]);
  if (!total) return <span className="pro__muted">no game</span>;
  return (
    <span className="pro-champ__split">
      {entries.map(([r, n]) => (
        <span key={r} className="pro-champ__split-item">
          {r} {Math.round((n / total) * 100)}%
          <span className="pro__muted"> ({n})</span>
        </span>
      ))}
    </span>
  );
}

function Champions() {
  const { champions, patches } = useProIndex();
  const initialParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  );
  const [query, setQuery] = useState(initialParams.get("champion") || "");
  const [champion, setChampion] = useState(initialParams.get("champion") || "");
  const [patch, setPatch] = useState("");
  const [role, setRole] = useState(initialParams.get("role") || "");
  const [weeks, setWeeks] = useState(2);
  const [minRecent, setMinRecent] = useState(5);
  const [report, setReport] = useState<ChampionReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [partnerRoleCross, setPartnerRoleCross] = useState("");
  const [partnerRoleSyn, setPartnerRoleSyn] = useState("");

  const load = useCallback(
    (name: string) => {
      if (!name) return;
      setLoading(true);
      setError(null);
      getChampionReport(name, { patch: patch || undefined, min_n: 5, weeks })
        .then(setReport)
        .catch((e) => {
          setReport(null);
          setError(e.message || "Error");
        })
        .finally(() => setLoading(false));
    },
    [patch, weeks],
  );

  useEffect(() => {
    if (champion) load(champion);
  }, [champion, load]);

  // Keep the URL shareable (/pro/champions?champion=X&role=Y) WITHOUT going
  // through the router: a router navigation would remount the whole section
  // (AnimatePresence keys routes by location) and reset every filter.
  useEffect(() => {
    const next = new URLSearchParams();
    if (champion) next.set("champion", champion);
    if (role) next.set("role", role);
    const qs = next.toString();
    const target = qs ? `?${qs}` : window.location.pathname;
    if (window.location.search !== (qs ? `?${qs}` : "")) {
      window.history.replaceState(null, "", target);
    }
  }, [champion, role]);

  const champInputId = useId();
  const roleSelectId = useId();
  const weeksId = useId();
  const minRecentId = useId();
  const usePatch = !!patch;

  const nLabel = usePatch ? `N patch ${patch}` : `N ${weeks}w`;
  const nTooltip = usePatch ? TOOLTIPS.nPatch : TOOLTIPS.n;
  const nOf = useCallback(
    (it: ChampionReportItem) => (usePatch ? it.n_patch : it.n_window),
    [usePatch],
  );

  const splash = report ? splashUrl(report.champion) : null;
  const splashFb = report ? splashFallbackUrl(report.champion) : null;

  const byOwnRole = useCallback(
    (items: ChampionReportItem[]) =>
      role ? items.filter((it) => it.role === role) : items,
    [role],
  );
  const byPartnerRole = (items: ChampionReportItem[], pr: string) =>
    pr ? items.filter((it) => it.partner_role === pr) : items;

  const partnerRoleSelect = (
    value: string,
    onChange: (v: string) => void,
    label: string,
  ) => (
    <span className="pro__field">
      {label}
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">All roles</option>
        {ROLES.map((r) => (
          <option key={r} value={r}>
            {r}
          </option>
        ))}
      </select>
    </span>
  );

  const needRole = (
    <div className="pro__empty">
      Assign a role to the champion to see the indirect interactions.
    </div>
  );

  return (
    <div className="pro__page">
      <div className="pro__page-head">
        <h1>Champions</h1>
      </div>
      <p className="pro__page-sub">
        Pick a champion to see everything the model knows about it: activity,
        solo strength per role, and the matchups and synergies it wins or loses
        — each rated as a <b>percentile within its own interaction type</b>, so
        a rare matchup and a popular champion stay comparable.
      </p>

      <div className="pro__toolbar">
        <label className="pro__field" htmlFor={champInputId}>
          Champion
          <input
            id={champInputId}
            type="text"
            list="pro-champions"
            value={query}
            placeholder="e.g. Lucian"
            style={{ minWidth: 180 }}
            onChange={(e) => {
              setQuery(e.target.value);
              const match = champions.find(
                (c) => c.toLowerCase() === e.target.value.trim().toLowerCase(),
              );
              if (match) setChampion(match);
            }}
          />
        </label>
        <ChampionDatalist id="pro-champions" champions={champions} />
        <PatchSelect patches={patches} value={patch} onChange={setPatch} />
        <label className="pro__field" htmlFor={roleSelectId}>
          Role
          <select
            id={roleSelectId}
            value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            <option value="">Not set</option>
            {(report?.roles?.length ? report.roles : ROLES).map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label
          className="pro__field"
          htmlFor={minRecentId}
          data-tip="Interactions with fewer recent games than this are hidden from the tables."
        >
          Min
          <input
            id={minRecentId}
            type="number"
            min={0}
            value={minRecent}
            onChange={(e) =>
              setMinRecent(Math.max(0, parseInt(e.target.value || "0", 10)))
            }
          />
          games in the last
          <input
            id={weeksId}
            type="number"
            min={1}
            max={520}
            value={weeks}
            disabled={usePatch}
            data-tip={
              usePatch
                ? "With a patch dataset the tables count games during that patch instead."
                : "Size of the recent window, in weeks. The N column of every table counts games over this window."
            }
            onChange={(e) =>
              setWeeks(
                Math.max(1, Math.min(520, parseInt(e.target.value || "1", 10))),
              )
            }
          />
          weeks
        </label>
      </div>

      {error && <div className="pro__card pro__empty">{error}</div>}
      {loading && <div className="pro__card pro__empty">Loading…</div>}

      {!report && !loading && !error && (
        <div className="pro__card">
          <div className="pro__empty">
            Type a champion name above to open its report.
          </div>
        </div>
      )}

      {report && !loading && (
        <>
          <div
            className="pro__card"
            style={{ marginBottom: 18, position: "relative" }}
          >
            {splash && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `linear-gradient(90deg, rgba(27,29,34,0.97) 34%, rgba(27,29,34,0.72) 68%, rgba(27,29,34,0.3)), url(${splash}), url(${splashFb})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center 20%",
                }}
              />
            )}
            <div style={{ position: "relative", padding: "22px 22px 18px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: 14,
                  flexWrap: "wrap",
                }}
              >
                <h2
                  style={{
                    margin: 0,
                    fontFamily: "Montserrat, Inter, sans-serif",
                    fontSize: 26,
                  }}
                >
                  {report.champion}
                </h2>
                {usePatch && (
                  <span className="pro__muted">
                    percentiles from the dataset up to patch {patch}
                  </span>
                )}
              </div>

              <div className="pro-champ__stats">
                <div className="pro-champ__stat">
                  <span
                    className="pro-champ__stat-h"
                    data-tip="Competitive games (tiers 1-3) over the selected recent window, and how they split across roles."
                  >
                    Last {report.games.weeks} weeks —{" "}
                    {report.games.total_window} games
                  </span>
                  <RoleSplit
                    total={report.games.total_window}
                    roles={report.games.roles_window}
                  />
                </div>
                <div className="pro-champ__stat">
                  <span
                    className="pro-champ__stat-h"
                    data-tip="All competitive games (tiers 1-3) in the dataset, and how they split across roles."
                  >
                    All time — {report.games.total_all} games
                  </span>
                  <RoleSplit
                    total={report.games.total_all}
                    roles={report.games.roles_all}
                  />
                </div>
                <div className="pro-champ__stat">
                  <span
                    className="pro-champ__stat-h"
                    data-tip="How much the champion lifts a draft on its own, per role — percentile among all champion-roles."
                  >
                    Solo strength
                  </span>
                  <span className="pro-champ__split">
                    {byOwnRole(report.self).length ? (
                      byOwnRole(report.self).map((it) => (
                        <span key={it.role} className="pro-champ__split-item">
                          {it.role}{" "}
                          <b style={{ color: pctColor(it.pct) }}>
                            {it.pct == null ? "—" : it.pct}
                          </b>
                        </span>
                      ))
                    ) : (
                      <span className="pro__muted">no rated role</span>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="pro-champ__tables">
            <div className="pro__card">
              <div className="pro__card-title">
                Direct matchups
                <span className="pro__card-note">
                  High percentile = {report.champion}
                  {role ? ` (${role})` : ""} beats this champion.
                </span>
              </div>
              <div className="pro__scroll" style={{ maxHeight: 480 }}>
                <InteractionTable
                  items={byOwnRole(report.matchups_direct)}
                  nLabel={nLabel}
                  nTooltip={nTooltip}
                  nOf={nOf}
                  minRecent={minRecent}
                />
              </div>
            </div>

            <div className="pro__card">
              <div className="pro__card-title">
                <span
                  style={{ display: "flex", alignItems: "center", gap: 14 }}
                >
                  Cross-role matchups
                  {role &&
                    partnerRoleSelect(
                      partnerRoleCross,
                      setPartnerRoleCross,
                      "Opponent role",
                    )}
                </span>
                <span className="pro__card-note">
                  High percentile = {report.champion}
                  {role ? ` (${role})` : ""} beats this champion from another
                  lane.
                </span>
              </div>
              <div className="pro__scroll" style={{ maxHeight: 480 }}>
                {!role ? (
                  needRole
                ) : (
                  <InteractionTable
                    items={byPartnerRole(
                      byOwnRole(report.matchups_indirect),
                      partnerRoleCross,
                    )}
                    nLabel={nLabel}
                    nTooltip={nTooltip}
                    nOf={nOf}
                    minRecent={minRecent}
                  />
                )}
              </div>
            </div>

            <div className="pro__card">
              <div className="pro__card-title">
                <span
                  style={{ display: "flex", alignItems: "center", gap: 14 }}
                >
                  Synergies
                  {role &&
                    partnerRoleSelect(
                      partnerRoleSyn,
                      setPartnerRoleSyn,
                      "Teammate role",
                    )}
                </span>
                <span className="pro__card-note">
                  High percentile = {report.champion}
                  {role ? ` (${role})` : ""} performs well with this champion.
                </span>
              </div>
              <div className="pro__scroll" style={{ maxHeight: 480 }}>
                {!role ? (
                  needRole
                ) : (
                  <InteractionTable
                    items={byPartnerRole(
                      byOwnRole(report.synergies),
                      partnerRoleSyn,
                    )}
                    nLabel={nLabel}
                    nTooltip={nTooltip}
                    nOf={nOf}
                    minRecent={minRecent}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default Champions;
